// OLT Issues — the pre-computed per-ONU issue snapshots (no_username,
// duplicate_username, dead_onu, wrong_username, switch_mismatch, …).
// JSON sibling of the legacy /v1/frag/oltlog/main HTML page. Go side:
// internal/oltlog. Read-only list + a `feedback` action (operator
// verdict on a recommendation).

import type { ApiClient } from "./client.js";

export type OltIssueType =
  | "duplicate_username"
  | "no_username"
  | "dead_onu"   // optical link offline 15+ days — remove from OLT
  | "idle_onu"   // optical up but no PPPoE customer 15+ days — review
  | "wrong_username"
  | "single_user_switch_mismatch"
  | "switch_mismatch"
  | "no_switch_info";

/** One issue row (internal/oltlog/domain.go Issue). Each `type` uses a
 *  different subset of the fields — the rest are absent/zero. */
export interface OltIssue {
  type: OltIssueType | string;
  olt: string;
  olt_ip: string;
  onu_name: string;
  is_online: boolean;
  first_seen_at?: string;
  seen_at?: string;
  cpe_macs?: string[];
  cpe_users?: string[];
  username?: string;
  suggested_username?: string;
  suggested_usernames?: string[];
  duplicate?: string;
  kept?: string;
  last_active_ts?: number;
  last_active_age?: number;
  expected_switch?: string;
  users_behind?: string[];
  behind_switches?: string[];
  actual_user?: string;
  actual_user_status?: string;
  suggested_user_status?: string;
  /** Live PPPoE state ("online" / "offline" / "blocked" / "unknown") —
   *  drives the status dot next to a displayed username. */
  actual_user_state?: string;
  suggested_user_state?: string;
  /** Parallel to suggested_usernames for the multi-MAC no_username case
   *  (one CPE bridging several MACs, each suggesting its own username).
   *  Indices align with suggested_usernames / cpe_macs. */
  suggested_user_statuses?: string[];
  suggested_user_states?: string[];
  // Render-time enrichments (added Go-side for the React page):
  suggested_switch?: string;
  suggested_switches?: string[];
  users_behind_switches?: string[];
  desc_status?: string;
  mac_vendors?: Record<string, string>;
  // API-only computed fields (mirror IssueRow's per-row decisions):
  recommended?: string; // verbatim — round-trip this to /feedback
  /** "assign" → batch Auto-Add; "clear" → batch Remove-Binding; "" → not batch-selectable. */
  select_kind?: "assign" | "clear" | "";
  desc_target?: string; // for select_kind="assign": the desc to set (PPPoE username or dash-form switch name)
  pon?: string; // parsed PON port; "" if onu_name isn't canonical
  onu?: string; // parsed ONU index
}

export interface OltIssueCounts {
  duplicate_username: number;
  no_username: number;
  dead_onu: number;
  idle_onu: number;
  wrong_username: number;
  single_user_switch_mismatch: number;
  switch_mismatch: number;
  no_switch_info: number;
  total: number;
}

export interface OltIssuesResponse {
  ok: boolean;
  counts?: OltIssueCounts;
  issues?: OltIssue[];
  total?: number;
  visible?: number;
  has_duplicates?: boolean;
  error?: string;
}

export interface OltIssueFeedbackResult {
  ok: boolean;
  error?: string;
}

export function makeOltIssuesApi(client: ApiClient) {
  return {
    /** Filtered issue list + per-type counts. Filters: OLT name / type / free-text q. */
    list(filters: { olt?: string; type?: string; q?: string } = {}): Promise<OltIssuesResponse> {
      return client.get<OltIssuesResponse>("/v1/api/oltlog/list", {
        olt: filters.olt,
        type: filters.type,
        q: filters.q,
      });
    },
    /** Record an operator's verdict on a system recommendation. */
    feedback(args: {
      olt: string;
      onu: string;
      type: string;
      recommended: string;
      verdict: "correct" | "wrong";
      notes?: string;
    }): Promise<OltIssueFeedbackResult> {
      return client.post<OltIssueFeedbackResult>("/v1/api/oltlog/feedback", args);
    },
  };
}

export type OltIssuesApi = ReturnType<typeof makeOltIssuesApi>;
